import Vue from 'vue'
import configs from '@/config'
import textUtil from '@/utils/textUtil'
import store from '@/vuex/store'
import SystemConnection, { CONNECTION_STATUS } from '@/utils/system/SystemConnection'
import { SimpleCache } from '@/utils/cache.utils'

let resource = null

export const actions = {
  patchUserById: {
    method: 'PATCH',
    url: `${configs.baseUrl}/api/users/{userId}`,
  },
  getUser: {
    method: 'GET',
    url: `${configs.baseUrl}/api/users/get-profile/`,
  },
  getUserWithStripeSnapshot: {
    method: 'GET',
    url: `${configs.baseUrl}/api/users/get-profile/?useSnapshotStripeInfo=true`,
  },
  getUsers: {
    method: 'GET',
    url: `${configs.baseUrl}/api/users?referralCode={referralCode}`,
  },
  createPresenter: {
    method: 'POST',
    url: `${configs.baseUrl}/api/users/create-presenter`,
  },
  getTeamMembers: {
    method: 'GET',
    url: `${configs.baseUrl}/api/users/account/`,
  },
  removeUserAccount: {
    method: 'DELETE',
    url: `${configs.baseUrl}/api/users/`,
  },
  getTeamMembersV2: {
    method: 'GET',
    url: `${configs.baseUrl}/api/users/account/v2`,
  },
  logoutEveryWhere: {
    method: 'POST',
    url: `${configs.baseUrl}/api/users/{userId}/logout`,
  },
  getReferrees: {
    method: 'GET',
    url: `${configs.baseUrl}/api/users/referrees/`,
  },
  createGuest: {
    method: 'POST',
    url: `${configs.baseUrl}/api/users/create-guest`,
  },
  updateSettings: {
    method: 'PUT',
    url: `${configs.baseUrl}/api/users/update-settings`,
  },
  getSuggestedTeamsToJoin: {
    method: 'GET',
    url: `${configs.baseUrl}/api/users/get-teams-to-join`,
  },
  getStatistics: {
    method: 'GET',
    url: `${configs.baseUrl}/api/users/statistics`,
  },
  getPlusLegacyCoupon: {
    method: 'GET',
    url: `${configs.baseUrl}/api/users/plus-legacy/`,
  },
  getPrivateFeedbacks: {
    method: 'GET',
    url: `${configs.baseUrl}/api/private-feedbacks`,
  },
  setUserSetting: {
    method: 'PATCH',
    url: `${configs.baseUrl}/api/user-setting`,
  },
}

const initResource = () => {
  resource = Vue.resource('', {}, actions)
}

const patchUserById = (userId, params) => {
  return resource.patchUserById({ userId }, params)
}

const arrUserFieldSanitize = ['titleHTML']
const sanitizeUserField = (res) => {
  arrUserFieldSanitize.forEach((field) => {
    res.body[field] = textUtil.sanitize(res.body[field])
  })
}

const cache = new SimpleCache()

const getUser = async () => {
  const params = { segmented: true }
  const res = await resource.getUserWithStripeSnapshot(params)
  sanitizeUserField(res)
  return res.body
}

const getUserWithCache = async ({ maxAgeInSeconds = 1 }) => {
  return cache.invoke(() => {
    return getUser()
  }, {
    key: 'getUser',
    maxAgeInSeconds,
  })
}

const getUsers = async (params) => {
  const res = await resource.getUsers(params)
  return res.body.users
}

const createPresenter = async (params) => {
  const res = await resource.createPresenter(params)
  return res.body
}

const getTeamMembers = async (params) => {
  const res = await resource.getTeamMembers(params)
  return res.body
}

const getTeamMembersV2 = async (params) => {
  const res = await resource.getTeamMembersV2(params)
  return res.body
}

const logoutEveryWhere = async (userId, params) => {
  const res = await resource.logoutEveryWhere({ userId }, params)
  return res.body
}

const getReferrees = async (params) => {
  const res = await resource.getReferrees(params)
  return res.body
}

const createGuest = async (params) => {
  const res = await resource.createGuest(params)
  return res.body
}

const updateSettings = async (params) => {
  try {
    const res = await resource.updateSettings(params)
    SystemConnection.detectGeneralConnection(CONNECTION_STATUS.GOOD)
    return res
  } catch (error) {
    SystemConnection.detectGeneralConnection(CONNECTION_STATUS.UNSTABLE)
    return { body: { error: error.message } }
  }
}

const getSuggestedTeamToJoin = async (params) => {
  const res = await resource.getSuggestedTeamsToJoin(params)
  return res.body
}

const getStatistics = async () => {
  return resource.getStatistics()
}

const getPlusLegacyCoupon = async () => {
  return resource.getPlusLegacyCoupon()
}

const getPrivateFeedbacks = async (params) => {
  if (store.getters['homePage/canUseImprovedRecentFeedback']) {
    params.fromMyEditedPresentations = true
  }
  const res = await resource.getPrivateFeedbacks(params)
  return res.body
}
const setUserSetting = async (params) => {
  const res = await resource.setUserSetting(params)
  return res.body
}

const removeUserAccount = async (params) => {
  const res = await resource.removeUserAccount({}, params)
  return res.body
}

export default {
  getUser,
  getUserWithCache,
  initResource,
  patchUserById,
  createPresenter,
  getTeamMembers,
  getTeamMembersV2,
  logoutEveryWhere,
  getUsers,
  getReferrees,
  createGuest,
  updateSettings,
  getSuggestedTeamToJoin,
  getStatistics,
  getPlusLegacyCoupon,
  getPrivateFeedbacks,
  setUserSetting,
  removeUserAccount,
}
